<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import GoogleCalendarAPI from 'dashboard/api/googleCalendar';
import SectionLayout from 'dashboard/routes/dashboard/settings/account/components/SectionLayout.vue';
import { CALENDAR_COLORS } from 'dashboard/modules/agenda/agendaColors';

const { t } = useI18n();
const status = ref({ connected: false });
const isLoading = ref(false);
const justConnected = ref(false);
const justDisconnected = ref(false);
const justError = ref(false);
const isConfirmingDisconnect = ref(false);

async function fetchStatus() {
  try {
    const { data } = await GoogleCalendarAPI.getStatus();
    status.value = data;
  } catch {
    status.value = { connected: false };
  }
}

async function selectColor(color) {
  const previousColor = status.value.color;
  status.value = { ...status.value, color };
  try {
    await GoogleCalendarAPI.updateColor(color);
  } catch {
    status.value = { ...status.value, color: previousColor };
  }
}

async function connect() {
  isLoading.value = true;
  try {
    const { data } = await GoogleCalendarAPI.getAuthUrl();
    window.location.href = data.url;
  } catch {
    isLoading.value = false;
  }
}

async function disconnect() {
  isLoading.value = true;
  try {
    await GoogleCalendarAPI.disconnect();
    status.value = { connected: false };
    justDisconnected.value = true;
    setTimeout(() => {
      justDisconnected.value = false;
    }, 3000);
  } finally {
    isLoading.value = false;
    isConfirmingDisconnect.value = false;
  }
}

onMounted(async () => {
  const param = new URLSearchParams(window.location.search).get(
    'google_calendar'
  );
  if (param === 'connected') {
    justConnected.value = true;
    setTimeout(() => {
      justConnected.value = false;
    }, 8000);
  } else if (param === 'error') {
    justError.value = true;
    setTimeout(() => {
      justError.value = false;
    }, 8000);
  }
  await fetchStatus();
});
</script>

<template>
  <SectionLayout with-border>
    <template #title>
      <span class="i-lucide-calendar size-5 text-n-brand" />
      {{ t('AGENDA.GOOGLE_CALENDAR.TITLE') }}
    </template>
    <template #description>
      {{ t('AGENDA.GOOGLE_CALENDAR.DESCRIPTION') }}
    </template>
    <template v-if="status.connected" #headerActions>
      <p class="text-n-slate-11 text-body-main mb-2">
        {{ t('AGENDA.GOOGLE_CALENDAR.CHOOSE_COLOR_LABEL') }}
      </p>
      <div class="flex gap-2 overflow-x-auto max-w-full pb-1">
        <button
          v-for="color in CALENDAR_COLORS"
          :key="color"
          type="button"
          class="size-6 rounded-full flex items-center justify-center shrink-0"
          :style="{ backgroundColor: color }"
          @click="selectColor(color)"
        >
          <span
            v-if="status.color === color"
            class="i-lucide-check size-3 text-white"
          />
        </button>
      </div>
    </template>

    <div>
      <div
        v-if="justError"
        class="flex items-center gap-3 p-4 mb-4 bg-n-ruby-2 border border-n-ruby-6 rounded-lg"
      >
        <span class="i-lucide-alert-circle size-5 text-n-ruby-11" />
        <p class="text-sm font-medium text-n-ruby-11">
          {{ t('AGENDA.GOOGLE_CALENDAR.ERROR_NOTICE') }}
        </p>
      </div>

      <div
        v-if="justConnected"
        class="flex items-center gap-3 p-4 mb-4 bg-n-teal-2 border border-n-teal-6 rounded-lg"
      >
        <span class="i-lucide-check-circle size-5 text-n-teal-11" />
        <p class="text-sm font-medium text-n-teal-11">
          {{ t('AGENDA.GOOGLE_CALENDAR.CONNECTED_NOTICE') }}
        </p>
      </div>

      <div
        v-if="justDisconnected"
        class="flex items-center gap-3 p-4 mb-4 bg-n-amber-2 border border-n-amber-6 rounded-lg"
      >
        <span class="i-lucide-unlink size-5 text-n-amber-11" />
        <p class="text-sm font-medium text-n-amber-11">
          {{ t('AGENDA.GOOGLE_CALENDAR.DISCONNECTED_NOTICE') }}
        </p>
      </div>

      <div v-if="status.connected">
        <div class="flex items-start gap-3 flex-wrap">
          <div
            class="flex items-center gap-2 px-4 py-2 bg-n-teal-2 border border-n-teal-6 rounded-lg"
          >
            <span class="i-lucide-check-circle size-5 text-n-teal-11" />
            <p class="text-sm font-medium text-n-teal-11 whitespace-nowrap">
              {{ t('AGENDA.GOOGLE_CALENDAR.CONNECTED') }}
            </p>
          </div>
          <div class="flex items-center gap-3 px-4 py-2">
            <p v-if="status.email" class="text-xs text-n-slate-11">
              {{ status.email }}
            </p>
            <button
              v-if="!isConfirmingDisconnect"
              :disabled="isLoading"
              class="text-xs text-n-ruby-11 hover:underline disabled:opacity-40"
              @click="isConfirmingDisconnect = true"
            >
              {{ t('AGENDA.GOOGLE_CALENDAR.DISCONNECT') }}
            </button>
          </div>
        </div>

        <div
          v-if="isConfirmingDisconnect"
          class="flex items-center gap-3 mt-2 ml-4"
        >
          <button
            :disabled="isLoading"
            class="text-xs text-n-slate-11 hover:underline disabled:opacity-40"
            @click="isConfirmingDisconnect = false"
          >
            {{ t('AGENDA.GOOGLE_CALENDAR.DISCONNECT_CANCEL') }}
          </button>
          <button
            :disabled="isLoading"
            class="text-xs font-medium text-n-ruby-11 hover:underline disabled:opacity-40"
            @click="disconnect"
          >
            {{ t('AGENDA.GOOGLE_CALENDAR.DISCONNECT_CONFIRM') }}
          </button>
        </div>
      </div>

      <button
        v-else
        :disabled="isLoading"
        class="flex items-center gap-2 px-4 py-2 bg-n-brand text-white text-sm rounded-lg disabled:opacity-40"
        @click="connect"
      >
        <span class="i-lucide-calendar-plus size-4" />
        {{ t('AGENDA.GOOGLE_CALENDAR.CONNECT') }}
      </button>
    </div>
  </SectionLayout>
</template>
